import React from 'react';
import { Icon } from 'web-components';
import * as S from './styled';
import { ButtonProps as StyledButtonProps } from './styled'

type IconProp = string | React.ComponentType<any> | React.ReactElement

interface ButtonProps extends Omit<StyledButtonProps, 'icon'> {
  icon?: IconProp;
  iconSize?: string;
  iconColor?: string;
}

type Props = React.ComponentProps<'button'> &
  React.ComponentProps<'a'> &
  ButtonProps;

/**
 * Button
 * @version 2.0.0
 * @author [Ubayd Rahmonzoda](ubayd.rahmonzoda@gmail.com)
 */
const Button = React.forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const {
    isDisabled = false,
    isSelected = false,
    isLoading = false,
    children = null,
    className = '',
    inline = false,
    type = 'button',
    icon,
    iconSize = 'default',
    iconColor = 'inherit',
    spacing = 'default',
    appearance = 'default',
    onClick = () => { },
    ...restProps
  } = props;

  const renderIcon = () => {
    if (!icon) return null;

    if (typeof icon === 'string') {
      return (
        <Icon
          type={icon}
          size={iconSize}
          color={iconColor}
        />
      );
    }

    if (React.isValidElement(icon)) {
      return icon;
    }

    if (typeof icon === 'function') {
      const IconComponent = icon;
      return <IconComponent />;
    }

    return null;
  }

  return (
    <S.Button
      {...restProps}
      ref={ref}
      type={type}
      spacing={spacing}
      className={className}
      appearance={appearance}
      onClick={onClick}
      inline={inline}
      isSelected={isSelected}
      disabled={isDisabled}
    >
      {renderIcon()}
      {children && <span>{children}</span>}
      {isLoading && (
        <Icon
          type="fa fa-spinner fa-spin"
          color="inherit"
        />
      )}
    </S.Button>
  );
});

Button.displayName = 'Button';

export default Button;
